// Y-axis display range for a max-diff chart, ported from the coffee block (getDisplayRange). The
// exporters ask for it so a static image keeps the axis the researcher is looking at; the rendered
// chart answers first, and this maths is the fallback for a chart that has not drawn yet.
//
// Max-diff reads its items by batch, and the measure decides the axis: the two probability measures
// share a fixed hundred-point scale, while utility scores are signed and scale to the data, error
// bars included.

import { getErrData } from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/errData'

const NON_RANGE_CHART_MODES = ['map', 'none']

const PROBABILITY_VIEW_MODES = ['varyingProbability', 'probability']

const RAW_VIEW_MODE = 'raw'

// Utility scores are plotted as a plain amount, so the error bars are read off that one axis --
// percents play no part in this chart, whatever the report counts elsewhere.
const errorBarsOf = (value, confidenceLevels) =>
  getErrData({ amount: value.raw || 0, errLO: value.errLO, errHI: value.errHI }, 'amount', {
    ignorePercents: true,
    rType: 'cases',
    answerType: 'amount',
    confidenceLevels
  })

// How far the items reach, in the measure they are read in. Only utility scores can reach below
// zero, and only they carry error bars to widen the reach.
const calcReach = ({ answer, batches, viewMode, confidenceLevels }) =>
  (batches || []).reduce(
    (reach, batch) => {
      const value = answer[`b${batch?.position}`]
      if (null == value) return reach

      if (RAW_VIEW_MODE === viewMode) {
        const amount = value.raw || 0
        const { minErrValue, maxErrValue } = errorBarsOf(value, confidenceLevels)

        return {
          min: Math.min(reach.min, amount - (minErrValue || 0)),
          max: Math.max(reach.max, amount + (maxErrValue || 0))
        }
      }

      if (PROBABILITY_VIEW_MODES.includes(viewMode)) {
        return { min: reach.min, max: Math.max(reach.max, value[viewMode] || 0) }
      }

      return reach
    },
    { min: 0, max: 0 }
  )

// The axis the items are drawn against: rounded outwards, and standing at a hundred points for a
// question whose items all came out at nothing.
const calcBounds = ({ min, max }) => {
  const low = 0 > min ? -Math.ceil(Math.abs(min)) : 0

  if (0 >= max) return { low, high: 0 === low ? 100 : Math.ceil(Math.abs(low)) }

  return { low, high: Math.ceil(max) }
}

export const calcDisplayRange = ({
  chartMode,
  viewMode,
  zoomLevel,
  globalChartMode,
  answer = {},
  batches = [],
  confidenceLevels = []
} = {}) => {
  if (NON_RANGE_CHART_MODES.includes(chartMode) || NON_RANGE_CHART_MODES.includes(globalChartMode)) return null

  if (!['min', 'max'].includes(zoomLevel)) return zoomLevel ?? null

  const { low, high } = calcBounds(calcReach({ answer, batches, viewMode, confidenceLevels }))

  // zoomed out, the probability measures show their whole scale rather than the reach of the data
  if ('max' === zoomLevel && PROBABILITY_VIEW_MODES.includes(viewMode)) return [0, 100]

  return [low, high]
}
